package com.yendo.mx.ui.components

import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.EstadoCarga
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLDivElement

@OptIn(ExperimentalMaterial3Api::class)
@Composable
actual fun MapaSeleccionScreen(
    direccionInicial: String,
    direccionAEditar: com.yendo.mx.models.DireccionGuardada?,
    buscarGpsAlAbrir: Boolean,
    modoSoloSeleccion: Boolean,
    puedeCerrar: Boolean,
    onBack: () -> Unit,
    onConfirmar: (String, String, Double, Double, String, String, String, String) -> Unit
) {
    // 🚀 SISTEMA DE PASOS (1: Mover Pin en Mapa, 2: Detalles Formulario)
    var pasoActual by remember { mutableIntStateOf(if (direccionAEditar != null) 2 else 1) }

    // 🚀 MODO BÚSQUEDA ACTIVO
    var modoBusquedaActivo by remember { mutableStateOf(direccionInicial.isEmpty() && !buscarGpsAlAbrir && direccionAEditar == null) }
    val focusRequester = remember { FocusRequester() }
    val scope = rememberCoroutineScope()

    // 🚀 COLORES DARK/LIGHT MODE IDÉNTICOS A ANDROID E IOS
    val isDark = isSystemInDarkTheme()
    val colorFondoTarjetas = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorDivisor = if (isDark) Color(0xFF2C2C2E) else Color(0xFFF5F5F5)
    val colorBordeOutlined = if (isDark) Color(0xFF3A3A3C) else Color(0xFFE0E0E0)
    val bgFueraCobertura = if (isDark) Color(0xFF3E2723) else Color(0xFFFFEBEE)

    val estadoZona by ConfigManager.estadoConfig.collectAsState()
    val estaFueraDeCobertura = estadoZona is EstadoCarga.SinCobertura
    var cargandoInterno by remember { mutableStateOf(false) }
    val estaCargando = cargandoInterno || estadoZona is EstadoCarga.Cargando

    var latitud by remember { mutableStateOf(if (direccionAEditar != null && direccionAEditar.lat != 0.0) direccionAEditar.lat else 16.2361) }
    var longitud by remember { mutableStateOf(if (direccionAEditar != null && direccionAEditar.lng != 0.0) direccionAEditar.lng else -93.9031) }
    var direccionDetectada by remember { mutableStateOf(direccionAEditar?.direccion ?: direccionInicial.ifBlank { "Arriaga, Chiapas" }) }
    var cpDetectado by remember { mutableStateOf(direccionAEditar?.cp ?: "30470") }

    var textoBusqueda by remember { mutableStateOf("") }

    var tipoUbicacion by remember { mutableStateOf(direccionAEditar?.tipoUbicacion?.takeIf { it.isNotBlank() } ?: "Casa") }
    var tituloOtro by remember { mutableStateOf(if (direccionAEditar?.tipoUbicacion == "Otro") direccionAEditar.titulo else "") }
    var opcionEntrega by remember { mutableStateOf(direccionAEditar?.opcionEntrega?.takeIf { it.isNotBlank() } ?: "Entregar en puerta") }
    var indicaciones by remember { mutableStateOf(direccionAEditar?.indicaciones ?: "") }

    var mapInstance by remember { mutableStateOf<dynamic>(null) }

    // 🚀 EVALUACIÓN INICIAL DE POLÍGONO AL ABRIR EL MAPA
    LaunchedEffect(latitud, longitud) {
        ConfigManager.procesarUbicacion(latitud, longitud, cpDetectado)
    }

    // 🚀 CONTENEDOR DOM INTERACTIVO PARA GOOGLE MAPS (Se monta en el área libre entre buscador y tarjeta)
    DisposableEffect(pasoActual, modoBusquedaActivo) {
        if (pasoActual == 1 && !modoBusquedaActivo) {
            val mapDiv = document.createElement("div") as HTMLDivElement
            mapDiv.id = "gmap-selection-active"
            mapDiv.style.position = "absolute"
            mapDiv.style.top = "80px"
            mapDiv.style.left = "0px"
            mapDiv.style.width = "100%"
            mapDiv.style.height = "calc(100% - 240px)"
            mapDiv.style.zIndex = "100"
            document.body?.appendChild(mapDiv)

            try {
                val google = window.asDynamic().google
                if (google != undefined && google.maps != undefined) {
                    val centerObj = js("{}")
                    centerObj.lat = latitud
                    centerObj.lng = longitud

                    val mapOptions = js("{}")
                    mapOptions.center = centerObj
                    mapOptions.zoom = 16
                    mapOptions.mapTypeControl = false
                    mapOptions.streetViewControl = false
                    mapOptions.zoomControl = true

                    val map = js("new google.maps.Map(mapDiv, mapOptions)")
                    mapInstance = map

                    // Evento cuando se detiene el arrastre del mapa
                    map.addListener("idle", {
                        val center = map.getCenter()
                        if (center != null) {
                            val newLat = center.lat().unsafeCast<Double>()
                            val newLng = center.lng().unsafeCast<Double>()
                            latitud = newLat
                            longitud = newLng

                            // Evaluación de polígonos
                            scope.launch {
                                ConfigManager.procesarUbicacion(newLat, newLng, cpDetectado)
                            }

                            // Geocodificación inversa
                            try {
                                val geocoder = js("new google.maps.Geocoder()")
                                val req = js("{}")
                                val loc = js("{}")
                                loc.lat = newLat
                                loc.lng = newLng
                                req.location = loc

                                geocoder.geocode(req, { results: dynamic, status: dynamic ->
                                    if (status == "OK" && results != null && results.length > 0) {
                                        val address = results[0].formatted_address
                                        if (address != undefined && address != null) {
                                            direccionDetectada = address.unsafeCast<String>()
                                        }
                                    }
                                })
                            } catch (e: Throwable) {
                                println("YENDO_WEB: Geocoder error -> ${e.message}")
                            }
                        }
                    })
                }
            } catch (e: Throwable) {
                println("YENDO_WEB: Error cargando Google Maps -> ${e.message}")
            }

            onDispose {
                mapInstance = null
                mapDiv.remove()
            }
        } else {
            onDispose {}
        }
    }

    Box(modifier = Modifier.fillMaxSize().background(colorFondoTarjetas)) {

        // 🚀 1. VISTA DE MAPA Y CONTROLES FLOTANTES
        Box(modifier = Modifier.fillMaxSize()) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Transparent),
                contentAlignment = Alignment.Center
            ) {}

            // 🚀 PIN FIJO EN EL CENTRO DEL MAPA
            AnimatedVisibility(
                visible = !modoBusquedaActivo && pasoActual == 1,
                modifier = Modifier.align(Alignment.Center).offset(y = (-24).dp),
                enter = fadeIn(), exit = fadeOut()
            ) {
                Icon(Icons.Default.LocationOn, contentDescription = null, tint = RosaYendo, modifier = Modifier.size(48.dp))
            }

            // 🚀 BOTÓN GPS FLOTANTE
            AnimatedVisibility(
                visible = !modoBusquedaActivo && pasoActual == 1,
                modifier = Modifier.align(Alignment.BottomEnd).padding(end = 16.dp, bottom = 200.dp),
                enter = fadeIn(), exit = fadeOut()
            ) {
                FloatingActionButton(
                    onClick = {
                        val geo = window.navigator.asDynamic().geolocation
                        if (geo != undefined) {
                            geo.getCurrentPosition({ pos: dynamic ->
                                val userLat = pos.coords.latitude.unsafeCast<Double>()
                                val userLng = pos.coords.longitude.unsafeCast<Double>()
                                latitud = userLat
                                longitud = userLng

                                scope.launch {
                                    ConfigManager.procesarUbicacion(userLat, userLng, cpDetectado)
                                }

                                val targetMap = mapInstance
                                if (targetMap != null && targetMap != undefined) {
                                    val newCenter = js("{}")
                                    newCenter.lat = userLat
                                    newCenter.lng = userLng
                                    targetMap.setCenter(newCenter)
                                    targetMap.setZoom(17)
                                }
                            }, { err: dynamic ->
                                println("YENDO_WEB: Geolocation error -> ${err?.message}")
                            })
                        }
                    },
                    containerColor = colorFondoTarjetas,
                    contentColor = RosaYendo
                ) {
                    Icon(Icons.Default.MyLocation, contentDescription = "Mi Ubicación")
                }
            }

            // 🚀 TARJETA BOTTOM (PASO 1)
            AnimatedVisibility(
                visible = !modoBusquedaActivo && pasoActual == 1,
                modifier = Modifier.align(Alignment.BottomCenter).fillMaxWidth().padding(16.dp),
                enter = slideInVertically { it } + fadeIn(),
                exit = slideOutVertically { it } + fadeOut()
            ) {
                Card(
                    colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                    elevation = CardDefaults.cardElevation(12.dp),
                    shape = RoundedCornerShape(20.dp)
                ) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Text("Entregar en:", color = colorTextoSecundario, fontSize = 12.sp)
                        if (estaCargando) {
                            LinearProgressIndicator(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), color = RosaYendo)
                        } else {
                            Text(direccionDetectada, fontWeight = FontWeight.Bold, fontSize = 16.sp, maxLines = 2, color = colorTextoPrincipal)
                        }

                        Spacer(Modifier.height(16.dp))

                        // 🚀 AVISO DE FUERA DE ZONA (EVALUACIÓN DE POLÍGONO DE COBERTURA)
                        AnimatedVisibility(estaFueraDeCobertura) {
                            Row(
                                modifier = Modifier.fillMaxWidth().background(bgFueraCobertura, RoundedCornerShape(8.dp)).padding(12.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(Icons.Default.Warning, null, tint = Color.Red)
                                Spacer(Modifier.width(8.dp))
                                Text("Fuera de zona.", color = Color.Red, fontSize = 13.sp, fontWeight = FontWeight.Bold)
                            }
                        }
                        Spacer(Modifier.height(16.dp))

                        Button(
                            onClick = {
                                if (modoSoloSeleccion) {
                                    onConfirmar("", direccionDetectada, latitud, longitud, cpDetectado, "", "", "Arriaga")
                                } else {
                                    pasoActual = 2
                                }
                            },
                            modifier = Modifier.fillMaxWidth().height(50.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                            shape = RoundedCornerShape(12.dp),
                            enabled = !estaFueraDeCobertura && !estaCargando
                        ) {
                            Text(
                                if (modoSoloSeleccion) "Confirmar esta ubicación" else "Confirmar ubicación",
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )
                        }
                    }
                }
            }
        }

        // 🚀 2. CAPA DEL BUSCADOR PLACES
        Column(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .fillMaxWidth()
                .fillMaxHeight(if (modoBusquedaActivo) 1f else 0f)
                .background(if (modoBusquedaActivo) colorFondoTarjetas else Color.Transparent)
        ) {
            AnimatedVisibility(visible = pasoActual == 1, enter = fadeIn(), exit = fadeOut()) {
                Card(
                    shape = RoundedCornerShape(if (modoBusquedaActivo) 0.dp else 12.dp),
                    colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                    elevation = CardDefaults.cardElevation(if (modoBusquedaActivo) 0.dp else 8.dp),
                    modifier = Modifier.fillMaxWidth().padding(if (modoBusquedaActivo) 0.dp else 16.dp)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp, vertical = if (modoBusquedaActivo) 8.dp else 0.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (puedeCerrar || modoBusquedaActivo || direccionAEditar != null) {
                            IconButton(onClick = {
                                if (modoBusquedaActivo) modoBusquedaActivo = false else onBack()
                            }) {
                                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = null, tint = colorTextoPrincipal)
                            }
                        }

                        OutlinedTextField(
                            value = textoBusqueda,
                            onValueChange = {
                                textoBusqueda = it
                                if (it.isNotEmpty()) modoBusquedaActivo = true
                            },
                            modifier = Modifier.weight(1f).focusRequester(focusRequester),
                            placeholder = { Text("¿A dónde enviamos?", fontSize = 14.sp, color = colorTextoSecundario) },
                            singleLine = true,
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color.Transparent,
                                unfocusedBorderColor = Color.Transparent,
                                focusedTextColor = colorTextoPrincipal,
                                unfocusedTextColor = colorTextoPrincipal,
                                cursorColor = RosaYendo
                            ),
                            trailingIcon = {
                                if (textoBusqueda.isNotEmpty()) {
                                    IconButton(onClick = { textoBusqueda = "" }) {
                                        Icon(Icons.Default.Clear, contentDescription = null, tint = colorTextoSecundario)
                                    }
                                }
                            }
                        )
                    }
                }
            }

            AnimatedVisibility(visible = modoBusquedaActivo) {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                    shape = RoundedCornerShape(0.dp)
                ) {
                    LazyColumn(modifier = Modifier.fillMaxHeight()) {
                        item {
                            Row(
                                modifier = Modifier.fillMaxWidth().clickable {
                                    textoBusqueda = ""
                                    modoBusquedaActivo = false
                                }.padding(horizontal = 16.dp, vertical = 14.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(Icons.Default.Place, contentDescription = null, tint = colorTextoSecundario, modifier = Modifier.size(24.dp))
                                Spacer(Modifier.width(12.dp))
                                Text("Fijar pin en el mapa", fontWeight = FontWeight.Bold, fontSize = 15.sp, color = colorTextoPrincipal)
                            }
                            HorizontalDivider(color = colorDivisor)
                        }

                        item {
                            Row(
                                modifier = Modifier.fillMaxWidth().clickable {
                                    textoBusqueda = ""
                                    modoBusquedaActivo = false
                                    val geo = window.navigator.asDynamic().geolocation
                                    if (geo != undefined) {
                                        geo.getCurrentPosition({ pos: dynamic ->
                                            val userLat = pos.coords.latitude.unsafeCast<Double>()
                                            val userLng = pos.coords.longitude.unsafeCast<Double>()
                                            latitud = userLat
                                            longitud = userLng

                                            scope.launch {
                                                ConfigManager.procesarUbicacion(userLat, userLng, cpDetectado)
                                            }

                                            val targetMap = mapInstance
                                            if (targetMap != null && targetMap != undefined) {
                                                val newCenter = js("{}")
                                                newCenter.lat = userLat
                                                newCenter.lng = userLng
                                                targetMap.setCenter(newCenter)
                                                targetMap.setZoom(17)
                                            }
                                        })
                                    }
                                }.padding(horizontal = 16.dp, vertical = 14.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(Icons.Default.MyLocation, contentDescription = null, tint = RosaYendo, modifier = Modifier.size(24.dp))
                                Spacer(Modifier.width(16.dp))
                                Text("Usar mi ubicación actual", fontWeight = FontWeight.Bold, fontSize = 15.sp, color = RosaYendo)
                            }
                            HorizontalDivider(color = colorDivisor)
                        }
                    }
                }
            }
        }

        // 3. PASO 2: DETALLES DE ENTREGA
        AnimatedVisibility(
            visible = pasoActual == 2,
            enter = slideInVertically(initialOffsetY = { it }) + fadeIn(),
            exit = slideOutVertically(targetOffsetY = { it }) + fadeOut()
        ) {
            Scaffold(
                topBar = {
                    TopAppBar(
                        title = { Text("Detalles de entrega", fontWeight = FontWeight.Bold, color = colorTextoPrincipal) },
                        navigationIcon = {
                            IconButton(onClick = { if (direccionAEditar == null) pasoActual = 1 else onBack() }) {
                                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = null, tint = colorTextoPrincipal)
                            }
                        },
                        colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoTarjetas)
                    )
                },
                bottomBar = {
                    Surface(shadowElevation = 16.dp, color = colorFondoTarjetas) {
                        Button(
                            onClick = {
                                val finalTitulo = if (tipoUbicacion == "Otro") tituloOtro else tipoUbicacion
                                onConfirmar(finalTitulo, direccionDetectada, latitud, longitud, cpDetectado, tipoUbicacion, opcionEntrega, indicaciones)
                            },
                            modifier = Modifier.fillMaxWidth().padding(16.dp).height(52.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = RosaYendo,
                                disabledContainerColor = if (isDark) Color(0xFF333333) else Color(0xFFE0E0E0)
                            ),
                            shape = RoundedCornerShape(12.dp),
                            enabled = (tipoUbicacion != "Otro" || tituloOtro.isNotBlank()) && indicaciones.isNotBlank()
                        ) {
                            Text("Guardar y continuar", fontWeight = FontWeight.Bold, color = if (indicaciones.isNotBlank()) Color.White else colorTextoSecundario)
                        }
                    }
                },
                containerColor = colorFondoTarjetas
            ) { padding ->
                LazyColumn(modifier = Modifier.fillMaxSize().padding(padding)) {
                    item {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text(direccionDetectada, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                            Text("¿La ubicación es incorrecta? Toca aquí para ajustarla en el mapa.", color = RosaYendo, fontSize = 12.sp, modifier = Modifier.clickable { pasoActual = 1 }.padding(top = 4.dp))
                        }
                        HorizontalDivider(color = colorDivisor)
                    }

                    item {
                        Column(modifier = Modifier.padding(horizontal = 16.dp).padding(top = 16.dp)) {
                            Text("¿Qué lugar es?", fontWeight = FontWeight.Bold, color = colorTextoPrincipal, fontSize = 14.sp)
                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                BotonTipoUbicacionWeb("Casa", Icons.Default.Home, tipoUbicacion == "Casa", isDark) { tipoUbicacion = "Casa" }
                                BotonTipoUbicacionWeb("Trabajo", Icons.Default.Work, tipoUbicacion == "Trabajo", isDark) { tipoUbicacion = "Trabajo" }
                                BotonTipoUbicacionWeb("Otro", Icons.Default.Place, tipoUbicacion == "Otro", isDark) { tipoUbicacion = "Otro" }
                            }

                            if (tipoUbicacion == "Otro") {
                                OutlinedTextField(
                                    value = tituloOtro,
                                    onValueChange = { tituloOtro = it },
                                    label = { Text("Nombre personalizado (Ej. Casa Novia)", color = colorTextoSecundario) },
                                    modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp),
                                    shape = RoundedCornerShape(12.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedTextColor = colorTextoPrincipal,
                                        unfocusedTextColor = colorTextoPrincipal,
                                        focusedBorderColor = RosaYendo,
                                        unfocusedBorderColor = colorBordeOutlined,
                                        cursorColor = RosaYendo
                                    )
                                )
                            }

                            Text("Opción de entrega", fontWeight = FontWeight.Bold, color = colorTextoPrincipal, fontSize = 14.sp, modifier = Modifier.padding(top = 8.dp))
                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                BotonTipoUbicacionWeb("Entregar en puerta", Icons.Default.DoorFront, opcionEntrega == "Entregar en puerta", isDark) { opcionEntrega = "Entregar en puerta" }
                                BotonTipoUbicacionWeb("Dejar en la puerta", Icons.Default.SensorDoor, opcionEntrega == "Dejar en la puerta", isDark) { opcionEntrega = "Dejar en la puerta" }
                            }

                            Text("Indicaciones para el repartidor (Obligatorio)*", fontWeight = FontWeight.Bold, color = colorTextoPrincipal, fontSize = 14.sp, modifier = Modifier.padding(top = 8.dp))
                            OutlinedTextField(
                                value = indicaciones,
                                onValueChange = { indicaciones = it },
                                placeholder = { Text("Ej: Casa verde, portón negro, el timbre no sirve...", color = colorTextoSecundario) },
                                modifier = Modifier.fillMaxWidth().height(100.dp).padding(vertical = 8.dp),
                                shape = RoundedCornerShape(12.dp),
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedTextColor = colorTextoPrincipal,
                                    unfocusedTextColor = colorTextoPrincipal,
                                    focusedBorderColor = RosaYendo,
                                    unfocusedBorderColor = if (indicaciones.isBlank()) Color.Red.copy(alpha = 0.5f) else colorBordeOutlined,
                                    cursorColor = RosaYendo
                                )
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun BotonTipoUbicacionWeb(titulo: String, icono: ImageVector, seleccionado: Boolean, isDark: Boolean, onClick: () -> Unit) {
    val colorBg = if (seleccionado) RosaYendo else (if (isDark) Color(0xFF2C2C2E) else Color(0xFFF5F5F5))
    val colorContent = if (seleccionado) Color.White else (if (isDark) Color.LightGray else Color.Gray)

    Surface(
        onClick = onClick,
        modifier = Modifier.height(45.dp),
        shape = RoundedCornerShape(12.dp),
        color = colorBg,
        contentColor = colorContent
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            Icon(icono, contentDescription = null, modifier = Modifier.size(18.dp))
            Spacer(Modifier.width(6.dp))
            Text(titulo, fontWeight = FontWeight.Bold, fontSize = 13.sp)
        }
    }
}
